import { useThemeContext } from "@src/context/ThemeContext";
import type { ThemeIllustrationName } from "@src/interfaces/theme-config.interface";
import { IllustrationBrand } from "@uhg-abyss/web/ui/IllustrationBrand";
import type { ComponentProps, CSSProperties, FC } from "react";

type IllustrationBrandProps = ComponentProps<typeof IllustrationBrand>;

interface ThemedIllustrationProps {
  /** Key used to look up the illustration in the active theme's config */
  themeIllustrationName: ThemeIllustrationName | string;
  /** Alt / aria-label text used for all rendering paths */
  altText?: string;
  /**
   * Additional inline styles merged with the themed illustration style.
   * When the same CSS property exists in both, the themed illustration's
   * configured style takes precedence.
   * Only applies when a themed `<img>` is rendered.
   */
  themedImageStyle?: CSSProperties;
  /**
   * Local SVG/image src to use when no themed asset override is configured and
   * the illustration key maps to a custom asset that doesn't have an
   * `IllustrationBrand` equivalent (e.g. `/illustrations/interactive_counseling.svg`).
   */
  localSrc?: string;
  /** Style applied to the local-src `<img>` element */
  defaultLocalImageStyle?: CSSProperties;
  /**
   * Props forwarded to `IllustrationBrand` when no themed/local image is
   * rendered.
   */
  brandProps?: Omit<IllustrationBrandProps, "brand">;
}

/**
 * Renders an illustration that respects the active UTM-driven theme.
 *
 * Resolution order:
 * 1. If active theme explicitly marks the illustration hidden → `null`
 * 2. Else if the active theme has a configured image `src` for `themeIllustrationName` → themed `<img>`
 * 3. Else if `localSrc` is provided → local `<img>`
 * 4. Else if `brandProps` is provided → `<IllustrationBrand>`
 * 5. Otherwise → `null`
 */
export const ThemedIllustration: FC<ThemedIllustrationProps> = ({
  themeIllustrationName,
  altText,
  themedImageStyle,
  localSrc,
  defaultLocalImageStyle,
  brandProps,
}) => {
  const { activeThemeConfig } = useThemeContext();
  const themedIllustration =
    activeThemeConfig.assets.illustrations?.[themeIllustrationName as ThemeIllustrationName];

  if (themedIllustration?.hidden) {
    return null;
  }

  if (themedIllustration?.src) {
    return (
      <img
        src={themedIllustration.src}
        alt={themedIllustration.alt || altText || "Illustration"}
        style={{ ...themedImageStyle, ...themedIllustration.style }}
      />
    );
  }

  if (localSrc) {
    return <img src={localSrc} alt={altText || "Illustration"} style={defaultLocalImageStyle} />;
  }

  if (brandProps?.illustration) {
    return (
      <IllustrationBrand brand="optum" {...brandProps} altText={brandProps.altText || altText} />
    );
  }

  return null;
};
